import { useMemo, useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { ALL_ROWS, BRANDS, REPAIR_TYPES, formatPrice, tierInfo, waLink } from "./priceData";

const RowAlt = ({ tiers }) => (tiers.length === 0 ? <span className="block text-xs text-inkSoft/70">Preis per WhatsApp</span> : (
    <span className="block text-xs text-inkSoft/70">
        {[tierInfo(tiers[0][0]).label, ...tiers.slice(1).map(([k, p]) => `${tierInfo(k).label} ${p} €`)].join(" · ")}
    </span>
));

const BrandTable = ({ brand, rows, open }) => {
    const models = new Set(rows.map((r) => r.model)).size;
    return (
        <details open={open} className="group border-b border-ink/10" data-testid={`price-brand-${brand.toLowerCase()}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between py-5 font-display text-2xl md:text-3xl text-ink">
                <span>{brand}</span>
                <span className="flex items-center gap-3 font-body text-xs uppercase tracking-[0.2em] text-inkSoft">{models} Modelle <ChevronDown size={18} className="text-brand transition-transform group-open:rotate-180" /></span>
            </summary>
            <div className="overflow-x-auto pb-6">
                <table className="w-full min-w-[640px] text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.15em] text-inkSoft border-b border-ink/10">
                            <th className="py-3 pr-4 font-semibold">Modell</th><th className="py-3 pr-4 font-semibold">Reparatur</th><th className="py-3 pr-4 font-semibold">Preis</th><th className="py-3 pr-4 font-semibold">Dauer</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={`${r.model}-${r.name}`} className="border-b border-ink/5 hover:bg-brandSoft/50 transition-colors">
                                <th scope="row" className="py-3 pr-4 text-left font-medium text-ink">{r.model}</th>
                                <td className="py-3 pr-4">{r.name}</td>
                                <td className="py-3 pr-4"><b className="text-ink">{formatPrice(r.tiers)}</b><RowAlt tiers={r.tiers} /></td>
                                <td className="py-3 pr-4 text-inkSoft">{r.duration}</td>
                                <td className="py-3 text-right whitespace-nowrap">
                                    <a href={waLink(`Hallo RAPID Key & Go, ich interessiere mich für: ${r.model} – ${r.name} (${formatPrice(r.tiers)}).`)} target="_blank" rel="noopener noreferrer" className="text-brand font-semibold hover:underline">anfragen →</a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </details>
    );
};

export const PriceList = () => {
    const [q, setQ] = useState("");
    const [type, setType] = useState("");

    const rows = useMemo(() => {
        const t = q.trim().toLowerCase();
        return ALL_ROWS.filter((r) => (!type || r.name === type) && (!t || `${r.brand} ${r.model}`.toLowerCase().includes(t)));
    }, [q, type]);
    const filtering = Boolean(q.trim() || type);

    return (
        <section id="pr-alle-preise" className="py-20 md:py-24 bg-white" data-testid="price-list">
            <div className="max-w-[1200px] mx-auto px-6 md:px-12">
                <h2 className="font-display text-4xl md:text-5xl tracking-tight text-ink">Alle Preise auf einen Blick.</h2>
                <p className="mt-3 text-inkSoft">Modell suchen oder eine Marke aufklappen – alle Preise inkl. Ersatzteil, Einbau und MwSt.</p>

                <div className="mt-8 flex items-center gap-3 rounded-xl border border-ink/15 bg-paper px-4 focus-within:border-brand transition-colors">
                    <Search size={18} className="text-inkSoft" />
                    <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Modell suchen, z. B. iPhone 13 oder Galaxy S22" className="w-full bg-transparent py-4 text-sm outline-none" data-testid="price-list-search" />
                    {q && <button type="button" onClick={() => setQ("")} aria-label="Suche zurücksetzen" data-testid="price-list-search-clear"><X size={18} className="text-inkSoft" /></button>}
                </div>
                <p className="mt-2 text-xs text-inkSoft" data-testid="price-list-count">{filtering ? `${rows.length} Treffer` : `${ALL_ROWS.length} Preise für ${new Set(ALL_ROWS.map((r) => r.model)).size} Modelle`}</p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" role="group" aria-label="Nach Reparatur filtern">
                    {REPAIR_TYPES.map((t) => (
                        <button key={t.name} type="button" aria-pressed={type === t.name} onClick={() => setType(type === t.name ? "" : t.name)}
                            className={`text-left rounded-xl border p-4 transition-colors ${type === t.name ? "border-brand bg-brandSoft" : "border-ink/10 hover:border-brand"}`}
                            data-testid={`price-type-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <span className="block font-semibold text-ink">{t.name}</span>
                            <span className="block text-sm text-brand font-semibold">ab {t.min} € <span className="text-inkSoft font-normal">· {t.count} Modelle</span></span>
                            <span className="mt-1 block text-xs text-inkSoft leading-relaxed">{t.text}</span>
                        </button>
                    ))}
                </div>

                <div className="mt-10 border-t border-ink/10">
                    {BRANDS.map((b) => {
                        const br = rows.filter((r) => r.brand === b);
                        return br.length ? <BrandTable key={`${b}-${filtering}`} brand={b} rows={br} open={filtering} /> : null;
                    })}
                    {rows.length === 0 && (
                        <p className="py-8 text-inkSoft" data-testid="price-list-empty">
                            Dieses Modell steht nicht in der Liste – reparieren können wir es meistens trotzdem.{" "}
                            <a href={waLink(`Hallo RAPID Key & Go, ich suche einen Preis für: ${q}`)} target="_blank" rel="noopener noreferrer" className="text-brand font-semibold underline">Per WhatsApp anfragen</a>
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
};
